import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { Button } from '@panwds/react-ui';
import dayjs from '../utils/date';
import { getCalendarOpenToDate, utcDayCell, utcTodayDayClassName } from '../utils/calendarDay';
import UtcNotice from './UtcNotice';
import 'react-datepicker/dist/react-datepicker.css';
import '../../scss/dark-datepicker.css';

/**
 * A date-range picker popover anchored below a trigger element.
 *
 * Props:
 *   isOpen      – whether the popover is visible
 *   onClose     – called when dismissed without applying
 *   anchorRef   – ref to the element the popover is anchored below
 *   value       – { start: Date|null, end: Date|null } currently applied range
 *   onChange    – (start: Date, end: Date) => void  called when "Apply" is clicked
 *   maxDate     – latest selectable day. Defaults to UTC today; pass null to allow future days.
 */
const CustomDateRangePopover = ({ isOpen, onClose, anchorRef, value, onChange, maxDate: maxDateProp }) => {
    const [draftStart, setDraftStart] = useState(value?.start || null);
    const [draftEnd, setDraftEnd] = useState(value?.end || null);
    const popoverRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    // Stable open-to date so the calendar doesn't jump months during range selection.
    // Opens on the month of the applied range's last day (most recent month with a selection).
    const openToDateRef = useRef(getCalendarOpenToDate(value?.start, value?.end));

    // Sync draft state when popover opens or external value changes
    useEffect(() => {
        if (isOpen) {
            setDraftStart(value?.start || null);
            setDraftEnd(value?.end || null);
            openToDateRef.current = getCalendarOpenToDate(value?.start, value?.end);
        }
    }, [isOpen, value?.start, value?.end]);

    // Compute position below the anchor
    useEffect(() => {
        if (!isOpen || !anchorRef?.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
            top: rect.bottom + window.scrollY + 6,
            left: rect.left + window.scrollX,
        });
    }, [isOpen, anchorRef]);

    // Dismiss on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleMouseDown = (e) => {
            if (
                popoverRef.current?.contains(e.target) ||
                e.target.closest?.('.react-datepicker') ||
                anchorRef?.current?.contains(e.target)
            ) return;
            onClose();
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return () => document.removeEventListener('mousedown', handleMouseDown, true);
    }, [isOpen, onClose, anchorRef]);

    if (!isOpen) return null;

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setDraftStart(start);
        setDraftEnd(end || null);
    };

    const handleApply = () => {
        if (!draftStart) return;
        const effectiveEnd = draftEnd || draftStart;
        onChange(draftStart, effectiveEnd);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const startLabel = draftStart ? dayjs(draftStart).format('MMM D, YYYY') : '—';
    const endLabel = draftEnd ? dayjs(draftEnd).format('MMM D, YYYY') : '—';

    const popover = (
        <div
            ref={popoverRef}
            style={{
                position: 'absolute',
                top: position.top,
                left: position.left,
                zIndex: 9999,
                backgroundColor: 'var(--ssmu-page-bg-secondary, #111)',
                border: '1px solid var(--ssmu-divider-line, #333)',
                borderRadius: '6px',
                padding: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                minWidth: '300px',
            }}
        >
            <DatePicker
                selectsRange
                startDate={draftStart}
                endDate={draftEnd}
                openToDate={openToDateRef.current}
                dateFormatCalendar="MMM yyyy"
                inline
                onChange={handleDateChange}
                calendarClassName="h-fit flex flex-col"
                maxDate={maxDateProp === undefined ? utcDayCell(0) : (maxDateProp ?? undefined)}
                dayClassName={utcTodayDayClassName}
            />
            <div
                style={{
                    fontSize: '0.75rem',
                    fontFamily: '"lato", sans-serif',
                    color: 'var(--ssmu-text-secondary, #999)',
                    marginTop: '4px',
                    marginBottom: '8px',
                    textAlign: 'center',
                    letterSpacing: '0.01em',
                }}
            >
                {startLabel} – {endLabel}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <UtcNotice />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button
                    size="sm"
                    appearance="tertiary-clear"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    appearance="primary"
                    onClick={handleApply}
                    isDisabled={!draftStart}
                >
                    Apply
                </Button>
            </div>
        </div>
    );

    return createPortal(popover, document.body);
};

export default CustomDateRangePopover;
